<script lang="ts">
  import Navbar from "$lib/components/Navbar.svelte";
  import TrialBanner from "$lib/components/TrialBanner.svelte";
  import CashflowChart from "$lib/components/CashflowChart.svelte";
  import { auth } from "$lib/stores/auth.svelte";
  import { api } from "$lib/api/client";
  import { navigate } from "$lib/router.svelte";
  import { fmtBRL } from "$lib/utils/format";

  // ── Mês selecionado ──────────────────────────────────────
  const today = new Date();
  let selYear  = $state(today.getFullYear());
  let selMonth = $state(today.getMonth()); // 0-based

  const MONTHS = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho",
                  "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

  function prevMonth() {
    if (selMonth === 0) { selMonth = 11; selYear--; } else { selMonth--; }
  }
  function nextMonth() {
    if (selMonth === 11) { selMonth = 0; selYear++; } else { selMonth++; }
  }

  function dateRange(year: number, month: number) {
    const from = `${year}-${String(month + 1).padStart(2, "0")}-01`;
    const last = new Date(year, month + 1, 0).getDate();
    const to   = `${year}-${String(month + 1).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
    return { from, to };
  }

  // ── Estado ───────────────────────────────────────────────
  let txs       = $state<any[]>([]);
  let prevTxs   = $state<any[]>([]);
  let loading   = $state(true);

  // Dashboard stats (saldo total + vencimentos)
  let stats     = $state<any>(null);
  // Onboarding checklist
  let onboarding = $state<any>(null);
  let checklistDismissed = $state(localStorage.getItem("takite.checklist-dismissed") === "1");

  // 4.1 — Projeção de caixa
  let cashflow       = $state<any>(null);
  let cashflowHorizon = $state(30);
  let cashflowLoading = $state(false);

  // 4.5 — Gastos por membro (workspace família)
  let memberSpending = $state<any>(null);

  async function loadCashflow(ws: any) {
    cashflowLoading = true;
    try {
      cashflow = await api<any>(`/workspaces/${ws.id}/cashflow?horizon=${cashflowHorizon}`).catch(() => null);
    } finally { cashflowLoading = false; }
  }

  async function load() {
    loading = true;
    try {
      const { from, to } = dateRange(selYear, selMonth);
      const prevM = selMonth === 0 ? 11 : selMonth - 1;
      const prevY = selMonth === 0 ? selYear - 1 : selYear;
      const { from: pFrom, to: pTo } = dateRange(prevY, prevM);
      const ws = auth.workspaces[0];
      if (!ws) return;
      const isFamily = ws.type === "family";
      const [rCur, rPrev, rStats, rOnb, rMember] = await Promise.all([
        api<{ items: any[] }>(`/transactions?limit=500&date_from=${from}&date_to=${to}`),
        api<{ items: any[] }>(`/transactions?limit=500&date_from=${pFrom}&date_to=${pTo}`),
        api<any>(`/workspaces/${ws.id}/dashboard`).catch(() => null),
        api<any>(`/workspaces/${ws.id}/onboarding`).catch(() => null),
        isFamily ? api<any>(`/workspaces/${ws.id}/member-spending`).catch(() => null) : Promise.resolve(null),
      ]);
      txs          = rCur.items;
      prevTxs      = rPrev.items;
      stats        = rStats;
      onboarding   = rOnb;
      memberSpending = rMember;
      // Carrega cashflow em paralelo (não bloqueia render)
      loadCashflow(ws);
    } finally { loading = false; }
  }

  $effect(() => {
    // Recarrega quando workspace ou mês muda. Lê selYear/selMonth para rastrear dependência.
    const _ws = auth.workspaces[0];
    const _y = selYear;
    const _m = selMonth;
    if (_ws) load();
  });

  $effect(() => {
    // Recarrega cashflow quando o horizon muda (após o primeiro load ter definido o workspace)
    const _h = cashflowHorizon;
    const ws = auth.workspaces[0];
    if (ws && !loading) loadCashflow(ws);
  });

  $effect(() => {
    function onWsChange() { load(); }
    window.addEventListener("takite:workspace-changed", onWsChange);
    return () => window.removeEventListener("takite:workspace-changed", onWsChange);
  });

  // ── Métricas ─────────────────────────────────────────────
  function calcMetrics(list: any[]) {
    let income = 0, expense = 0, pendingTotal = 0, pendingCount = 0;
    for (const t of list) {
      const v = Number(t.amount);
      if (t.kind === "income") income += v;
      else if (t.kind === "expense") expense += v;
      if (t.status === "pending" && t.kind === "expense") {
        pendingTotal += v; pendingCount++;
      }
    }
    return { income, expense, balance: income - expense, pendingTotal, pendingCount };
  }

  let metrics = $derived(() => calcMetrics(txs));
  let prevMetrics = $derived(() => calcMetrics(prevTxs));

  function fmtDelta(cur: number, prev: number): { pct: string; up: boolean | null } {
    if (prev === 0) return { pct: "—", up: null };
    const d = ((cur - prev) / prev) * 100;
    const sign = d >= 0 ? "+" : "";
    return { pct: `${sign}${d.toFixed(0)}%`, up: d >= 0 };
  }

  function goTransactionsPending() {
    navigate("/transactions");
    setTimeout(() => window.dispatchEvent(new CustomEvent("takite:filter-pending")), 100);
  }

  // Onboarding checklist
  let onbSteps = $derived(() => !onboarding ? [] : [
    { key: "has_account", label: "Criar uma conta bancária", done: onboarding.has_account, link: "/settings" },
    { key: "has_category", label: "Adicionar uma categoria", done: onboarding.has_category, link: "/categories" },
    { key: "has_transaction", label: "Registrar primeira transação", done: onboarding.has_transaction, link: "/transactions" },
    { key: "has_telegram", label: "Configurar o Telegram", done: onboarding.has_telegram, link: "/settings" },
  ]);

  let onbDoneCount = $derived(() => onbSteps().filter((s: any) => s.done).length);
  let showChecklist = $derived(() => onboarding && !onboarding.completed && !checklistDismissed);

  function dismissChecklist() {
    localStorage.setItem("takite.checklist-dismissed", "1");
    checklistDismissed = true;
  }
</script>

<Navbar />
<TrialBanner />
<main class="max-w-7xl mx-auto px-4 py-6 space-y-6">

  <!-- Cabeçalho + navegador de mês -->
  <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
    <div>
      <h1 class="text-2xl font-bold">Painel</h1>
      <p class="text-muted text-sm">Olá, {auth.user?.name ?? ""} 👋</p>
    </div>
    <div class="flex items-center gap-2">
      <button class="btn-ghost px-2 py-1 text-lg" onclick={prevMonth} aria-label="Mês anterior">‹</button>
      <span class="font-semibold text-sm min-w-[130px] text-center">
        {MONTHS[selMonth]} {selYear}
      </span>
      <button class="btn-ghost px-2 py-1 text-lg" onclick={nextMonth} aria-label="Próximo mês">›</button>
      <button class="btn-primary ml-2" onclick={() => navigate("/transactions")}>+ Nova</button>
    </div>
  </div>

  <!-- Checklist de onboarding -->
  {#if showChecklist()}
    <div class="card border-l-4 border-brand space-y-3">
      <div class="flex items-center justify-between">
        <div>
          <div class="font-semibold text-sm">Primeiros passos</div>
          <div class="text-xs text-muted">{onbDoneCount()} de {onbSteps().length} concluídos</div>
        </div>
        <button class="text-muted hover:text-current text-lg" onclick={dismissChecklist} aria-label="Fechar">×</button>
      </div>
      <div class="h-1.5 rounded-full overflow-hidden" style="background: var(--border)">
        <div class="h-full bg-brand transition-all duration-500"
             style="width: {(onbDoneCount() / onbSteps().length) * 100}%"></div>
      </div>
      <ul class="space-y-2">
        {#each onbSteps() as s}
          <li class="flex items-center gap-3 text-sm">
            <span class="flex-none w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold
                  {s.done ? 'bg-emerald-500 text-white' : 'border-2 border-[var(--border)]'}">
              {#if s.done}✓{/if}
            </span>
            {#if s.done}
              <span class="text-muted line-through">{s.label}</span>
            {:else}
              <button class="text-left hover:text-brand transition-colors"
                      onclick={() => navigate(s.link)}>{s.label} →</button>
            {/if}
          </li>
        {/each}
      </ul>
    </div>
  {/if}

  {#if loading}
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {#each [1,2,3] as _}<div class="skeleton h-24"></div>{/each}
    </div>
  {:else}
    <!-- Saldo total consolidado -->
    {#if stats}
      <div class="card"
           class:border-red-500={stats.expenses_exceed_income}
           class:border-l-4={stats.expenses_exceed_income}>
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div class="text-xs text-muted uppercase tracking-wide mb-1">💰 Saldo Total (todas as contas)</div>
            <div class="text-3xl font-bold"
                 class:text-emerald-500={Number(stats.total_balance) >= 0}
                 class:text-red-500={Number(stats.total_balance) < 0}>
              {fmtBRL(Number(stats.total_balance))}
            </div>
          </div>
          {#if stats.expenses_exceed_income}
            <div class="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              ⚠️ Despesas maiores que receitas este mês
            </div>
          {/if}
        </div>
      </div>
    {/if}

    <!-- Cards de resumo do mês -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div class="card">
        <div class="text-xs text-muted uppercase tracking-wide mb-1">Receitas</div>
        <div class="text-2xl font-bold text-emerald-500">{fmtBRL(metrics().income)}</div>
      </div>
      <div class="card">
        <div class="text-xs text-muted uppercase tracking-wide mb-1">Despesas</div>
        <div class="text-2xl font-bold text-red-500">{fmtBRL(metrics().expense)}</div>
      </div>
      <div class="card">
        <div class="text-xs text-muted uppercase tracking-wide mb-1">Saldo do Mês</div>
        <div class="text-2xl font-bold"
             class:text-emerald-500={metrics().balance >= 0}
             class:text-red-500={metrics().balance < 0}>
          {fmtBRL(metrics().balance)}
        </div>
      </div>
    </div>

    <!-- Vencimentos próximos 7 dias -->
    {#if stats?.upcoming_7d?.length > 0}
      <div class="card space-y-3">
        <div class="flex items-center justify-between">
          <div class="font-semibold text-sm">📅 Vencendo nos próximos 7 dias</div>
          <div class="text-sm font-bold text-amber-500">{fmtBRL(Number(stats.upcoming_total))}</div>
        </div>
        <ul class="divide-y" style="border-color: var(--border)">
          {#each stats.upcoming_7d as tx}
            <li class="py-2 flex items-center justify-between text-sm">
              <div>
                <div class="font-medium">{tx.description}</div>
                <div class="text-xs text-muted">
                  {tx.days_until === 0 ? "Hoje" : tx.days_until === 1 ? "Amanhã" : `Em ${tx.days_until} dias`}
                  · {tx.due_date.split("-").reverse().join("/")}
                </div>
              </div>
              <div class="font-semibold text-red-500">{fmtBRL(Number(tx.amount))}</div>
            </li>
          {/each}
        </ul>
        <button class="text-xs text-brand hover:underline" onclick={goTransactionsPending}>
          Ver todas as pendentes →
        </button>
      </div>
    {:else if metrics().pendingCount > 0}
      <!-- Card pendentes legado (sem due_date) -->
      <button
        class="w-full card flex items-center justify-between gap-3 text-left hover:opacity-80 transition-opacity cursor-pointer"
        onclick={goTransactionsPending}
      >
        <div class="flex items-center gap-3">
          <span class="text-2xl" aria-hidden="true">⏳</span>
          <div>
            <div class="font-semibold">Pendentes este mês</div>
            <div class="text-xs text-muted">{metrics().pendingCount} {metrics().pendingCount === 1 ? "conta a pagar" : "contas a pagar"}</div>
          </div>
        </div>
        <div class="text-right">
          <div class="font-bold text-amber-500">{fmtBRL(metrics().pendingTotal)}</div>
          <div class="text-xs text-muted">Ver →</div>
        </div>
      </button>
    {/if}

    <!-- Variação mês a mês -->
    {#if prevTxs.length > 0}
      {@const dIncome  = fmtDelta(metrics().income,  prevMetrics().income)}
      {@const dExpense = fmtDelta(metrics().expense, prevMetrics().expense)}
      {@const dBalance = fmtDelta(metrics().balance, prevMetrics().balance)}
      <div class="card">
        <div class="text-xs text-muted uppercase tracking-wide mb-3">📊 vs. mês anterior</div>
        <div class="grid grid-cols-3 gap-4 text-sm">
          <div>
            <div class="text-muted text-xs mb-0.5">Receitas</div>
            <div class="font-semibold text-emerald-500">{fmtBRL(metrics().income)}</div>
            <div class="text-xs mt-0.5"
                 class:text-emerald-400={dIncome.up === true}
                 class:text-red-400={dIncome.up === false}
                 class:text-muted={dIncome.up === null}>
              {dIncome.pct}{#if dIncome.up === true}↑{:else if dIncome.up === false}↓{/if}
            </div>
          </div>
          <div>
            <div class="text-muted text-xs mb-0.5">Despesas</div>
            <div class="font-semibold text-red-500">{fmtBRL(metrics().expense)}</div>
            <div class="text-xs mt-0.5"
                 class:text-red-400={dExpense.up === true}
                 class:text-emerald-400={dExpense.up === false}
                 class:text-muted={dExpense.up === null}>
              {dExpense.pct}{#if dExpense.up === true}↑⚠{:else if dExpense.up === false}↓{/if}
            </div>
          </div>
          <div>
            <div class="text-muted text-xs mb-0.5">Saldo</div>
            <div class="font-semibold"
                 class:text-emerald-500={metrics().balance >= 0}
                 class:text-red-500={metrics().balance < 0}>
              {fmtBRL(metrics().balance)}
            </div>
            <div class="text-xs mt-0.5"
                 class:text-emerald-400={dBalance.up === true}
                 class:text-red-400={dBalance.up === false}
                 class:text-muted={dBalance.up === null}>
              {dBalance.pct}{#if dBalance.up === true}↑{:else if dBalance.up === false}↓{/if}
            </div>
          </div>
        </div>
      </div>
    {/if}
  {/if}

  <!-- 4.1 — Projeção de caixa 30/60/90 dias -->
  {#if cashflow || cashflowLoading}
    <div class="mx-4 mb-4">
      {#if cashflowLoading && !cashflow}
        <div class="h-40 rounded-xl border border-slate-200 bg-white flex items-center justify-center text-slate-400 text-sm">
          Calculando projeção…
        </div>
      {:else if cashflow?.points?.length}
        <CashflowChart
          points={cashflow.points}
          alert={cashflow.alert}
          negativeInDays={cashflow.negative_in_days}
          horizon={cashflowHorizon}
          onHorizonChange={(h) => { cashflowHorizon = h; }}
        />
      {/if}
    </div>
  {/if}

  <!-- 4.5 — Gastos por membro (workspace família) -->
  {#if memberSpending?.members?.length > 1}
    <div class="mx-4 mb-4">
      <div class="rounded-xl border border-slate-200 bg-white shadow-sm p-4">
        <div class="flex items-center gap-2 mb-3">
          <span class="text-base">👨‍👩‍👧</span>
          <h3 class="text-sm font-semibold text-slate-700">Gastos por membro — {memberSpending.month}</h3>
        </div>
        <div class="space-y-3">
          {#each memberSpending.members as m}
            {@const exp = Number(m.expense)}
            {@const inc = Number(m.income)}
            {@const total = memberSpending.members.reduce((s: number, x: any) => s + Number(x.expense), 0)}
            {@const pct = total > 0 ? Math.round((exp / total) * 100) : 0}
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-sm font-bold text-indigo-600 shrink-0">
                {m.name.charAt(0).toUpperCase()}
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex justify-between items-baseline mb-1">
                  <span class="text-sm font-medium text-slate-700 truncate">{m.name}</span>
                  <span class="text-xs text-slate-500 ml-2">{m.tx_count} lanç.</span>
                </div>
                <div class="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div class="h-full bg-indigo-400 rounded-full transition-all" style="width:{pct}%"></div>
                </div>
                <div class="flex justify-between text-xs mt-0.5">
                  <span class="text-red-500">-{fmtBRL(exp)}</span>
                  {#if inc > 0}<span class="text-emerald-500">+{fmtBRL(inc)}</span>{/if}
                  <span class="text-slate-400">{pct}%</span>
                </div>
              </div>
            </div>
          {/each}
        </div>
      </div>
    </div>
  {/if}

</main>
